class TrProductStickyBar extends HTMLElement {
	selectors = {
		root: '[data-tr-js-product-sticky-bar-root]',
		stickyHeader: '[data-tr-js-sticky-header]',
		submitButton: '[data-tr-js-product-sticky-bar-submit]',
		submitButtonText: '[data-tr-js-product-sticky-bar-submit-text]',
		targetButton: '[data-tr-js-product-sticky-bar-target]',
	};

	attributes = {};

	stateClasses = {
		isVisible: 'is-visible',
	};

	connectedCallback() {
		this.productInfoElement = this.closest(this.selectors.root);
		this.stickyHeaderElement = document.querySelector(
			this.selectors.stickyHeader,
		);
		this.mobileMediaQuery = window.matchMedia('(max-width: 750px)');
		this.submitButtonElement = this.querySelector(this.selectors.submitButton);
		this.submitButtonTextElement = this.querySelector(
			this.selectors.submitButtonText,
		);
		this.targetButtonElement = this.productInfoElement?.querySelector(
			this.selectors.targetButton,
		);
		this.targetButtonTextElement = this.targetButtonElement?.querySelector('span');
		this.targetButtonObserver = null;
		this.animationFrame = null;
		this.variantChangeUnsubscriber = null;

		this.init();
		this.bindEvents();

		document.addEventListener('shopify:section:load', this.onSectionLoad);
	}

	disconnectedCallback() {
		document.removeEventListener('shopify:section:load', this.onSectionLoad);

		this.destroy();
	}

	init() {
		this.updateSubmitState();
		this.updateVisibility();
	}

	bindEvents() {
		this.addEventListener('click', this.onSubmitButtonClick);
		window.addEventListener('scroll', this.onWindowChange, { passive: true });
		window.addEventListener('resize', this.onWindowChange);
		this.mobileMediaQuery.addEventListener('change', this.onWindowChange);

		if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.variantChange) {
			this.variantChangeUnsubscriber = subscribe(
				PUB_SUB_EVENTS.variantChange,
				this.onVariantChange,
			);
		}

		this.observeTargetButton();
	}

	onSectionLoad = event => {
		if (!this.closest(`#shopify-section-${event.detail.sectionId}`)) return;

		this.cacheElements();
		this.init();
	};

	onVariantChange = event => {
		const sectionId = event?.data?.sectionId;

		if (!sectionId || sectionId !== this.dataset.sectionId) return;

		const updatedStickyBarElement = event.data.html.querySelector(
			`[data-tr-js-product-sticky-bar][data-section-id="${sectionId}"]`,
		);

		if (updatedStickyBarElement) {
			this.innerHTML = updatedStickyBarElement.innerHTML;
		}

		this.cacheElements();
		this.init();
	};

	onSubmitButtonClick = event => {
		const submitButtonElement = event.target.closest(this.selectors.submitButton);

		if (!submitButtonElement || submitButtonElement.disabled) return;

		this.targetButtonElement?.click();
		window.requestAnimationFrame(() => {
			this.updateSubmitState();
		});
	};

	onWindowChange = () => {
		if (this.animationFrame) return;

		this.animationFrame = window.requestAnimationFrame(() => {
			this.updateVisibility();
			this.animationFrame = null;
		});
	};

	updateSubmitState() {
		if (
			!this.submitButtonElement ||
			!this.submitButtonTextElement ||
			!this.targetButtonElement
		)
			return;

		const isDisabled =
			this.targetButtonElement.disabled ||
			this.targetButtonElement.getAttribute('aria-disabled') === 'true';
		const isLoading = this.targetButtonElement.classList.contains('loading');
		const targetSpinnerElement =
			this.targetButtonElement.querySelector('.loading__spinner');
		const submitSpinnerElement =
			this.submitButtonElement.querySelector('.loading__spinner');

		this.submitButtonElement.disabled = isDisabled;
		this.submitButtonElement.classList.toggle('loading', isLoading);
		this.submitButtonTextElement.textContent =
			this.targetButtonTextElement?.textContent.trim() || '';
		submitSpinnerElement?.classList.toggle(
			'hidden',
			targetSpinnerElement?.classList.contains('hidden') ?? !isLoading,
		);
	}

	updateVisibility() {
		if (!this.mobileMediaQuery.matches) {
			this.classList.remove(this.stateClasses.isVisible);
			return;
		}

		if (!this.targetButtonElement) {
			this.classList.remove(this.stateClasses.isVisible);
			return;
		}

		const stickyBarTop = this.getStickyBarTop();
		const targetRect = this.targetButtonElement.getBoundingClientRect();
		const isTargetVisible =
			targetRect.bottom > stickyBarTop && targetRect.top < window.innerHeight;

		this.style.setProperty('--trProductStickyBarTop', `${stickyBarTop}px`);
		this.classList.toggle(this.stateClasses.isVisible, !isTargetVisible);
		this.updateSubmitState();
	}

	cacheElements() {
		this.productInfoElement = this.closest(this.selectors.root);
		this.stickyHeaderElement = document.querySelector(
			this.selectors.stickyHeader,
		);
		this.mobileMediaQuery =
			this.mobileMediaQuery || window.matchMedia('(max-width: 750px)');
		this.submitButtonElement = this.querySelector(this.selectors.submitButton);
		this.submitButtonTextElement = this.querySelector(
			this.selectors.submitButtonText,
		);
		this.targetButtonElement = this.productInfoElement?.querySelector(
			this.selectors.targetButton,
		);
		this.targetButtonTextElement = this.targetButtonElement?.querySelector('span');
		this.observeTargetButton();
	}

	observeTargetButton() {
		this.targetButtonObserver?.disconnect();

		if (!this.targetButtonElement) return;

		this.targetButtonObserver = new MutationObserver(() => {
			this.updateSubmitState();
		});

		this.targetButtonObserver.observe(this.targetButtonElement, {
			attributes: true,
			attributeFilter: ['aria-disabled', 'class', 'disabled'],
			childList: true,
			subtree: true,
		});
	}

	getStickyBarTop() {
		const stickyHeaderBottom =
			this.stickyHeaderElement?.getBoundingClientRect().bottom;

		if (Number.isFinite(stickyHeaderBottom)) {
			return Math.max(stickyHeaderBottom, 0);
		}

		const headerHeight = getComputedStyle(document.documentElement)
			.getPropertyValue('--header-height')
			.trim();

		return parseFloat(headerHeight) || 0;
	}

	destroy() {
		this.removeEventListener('click', this.onSubmitButtonClick);
		window.removeEventListener('scroll', this.onWindowChange);
		window.removeEventListener('resize', this.onWindowChange);
		this.mobileMediaQuery?.removeEventListener('change', this.onWindowChange);
		this.variantChangeUnsubscriber?.();
		this.targetButtonObserver?.disconnect();

		if (this.animationFrame) {
			window.cancelAnimationFrame(this.animationFrame);
		}

		this.animationFrame = null;
		this.variantChangeUnsubscriber = null;
		this.targetButtonObserver = null;
		this.targetButtonElement = null;
		this.targetButtonTextElement = null;
		this.submitButtonElement = null;
		this.submitButtonTextElement = null;
		this.mobileMediaQuery = null;
		this.stickyHeaderElement = null;
		this.productInfoElement = null;
	}
}

if (!customElements.get('tr-product-sticky-bar')) {
	customElements.define('tr-product-sticky-bar', TrProductStickyBar);
}
